import { useRouter } from 'next/router';
import { useLocaleContext } from 'providers/LocaleProvider';
import { useViewContext } from 'providers/ViewContextProvider';
import React, { useRef } from 'react';
import SearchIcon from '@seaweb/coral/components/icons/Search';
import Select, {
  SelectSearch,
  SelectList,
  SelectOption,
} from '@seaweb/coral/components/Select';
import { useSearchContext } from 'providers/SearchProvider';
import ElkHighlighter from 'components/shared/ElkHighlighter';
import { object } from 'prop-types';
import styled from 'styled-components';

const StyledSelectSearchWrapper = styled.div`
  position: relative;
`;

const Placeholder = styled.div`
  opacity: 0.5;
  width: calc(100% - 20px);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding-left: 28px;
  color: #202325;
`;

const StyledSelectSearch = styled(SelectSearch)`
  background: transparent !important;
  border: solid 1px rgba(56, 56, 56, 0.15);
  border-radius: 4px;
  padding-left: 42px;

  @media screen and (-webkit-min-device-pixel-ratio: 0) {
    font-size: 16px;
  }

  @media screen and (min-width: 600px) and (max-width: 1024px) {
    max-width: calc(100vw - 440px);
  }

  ${({ isMobileView, isSearchFieldExpandInMobile }) => {
    if (isMobileView) {
      return isSearchFieldExpandInMobile
        ? `
          width: calc(100vw - 100px); 
          transition: 0.2s ease;`
        : `
          background-color: transparent; 
          color: white;`;
    }
  }}
`;

const StyledSearchIcon = styled(SearchIcon)`
  position: absolute;
  /* opacity: 0.5; */
  transform: translate(0, -50%);
  top: 50%;
  left: 16px;

  ${(props) => 'path {fill: #383838;opacity:0.25;}'}
`;

// const StyledSelectList = styled(SelectList)`
//   margin-top: 5px;
//   transform: none !important;
//   position: absolute !important;
//   top: 50px !important;
//   bottom: unset !important;
//   z-index: 5 !important;
// `;

function NavSearchField({ style }) {
  const { isMobileView } = useViewContext();
  const searchInputRef = useRef(null);
  const { push } = useRouter();
  const { locale: t } = useLocaleContext();
  const { keyword, setKeyword, articles } = useSearchContext();

  const handleSearch = () => push(`/search?keyword=${keyword || ''}&page=1`);

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
      handleSearch();
    }
  };

  const handleIconButton = () => {
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  const overrideResetText = (state, { action, nextState }) => {
    if (action.type === 'inputBlur') {
      const overrideState = nextState;
      overrideState.value = state.value;
      return overrideState;
    }
    return nextState;
  };

  return (
    <StyledSelectSearchWrapper>
      <Select
        searchValue={keyword}
        onSearch={(word) => setKeyword(word)}
        stateReducer={overrideResetText}
        onSelect={(val) => {
          if (searchInputRef.current) {
            searchInputRef.current.blur();
          }
          push(`/categories/${val[0]}/article/${val[1]}`);
        }}
      >
        <StyledSearchIcon
          isMobileView={isMobileView}
          isWhiteHeader
          size={isMobileView ? 16 : 20}
          onClick={handleIconButton}
        />

        <StyledSelectSearch
          isMobileView={isMobileView}
          ref={searchInputRef}
          style={{
            ...(style || {}),
          }}
          onKeyPress={handleKeyPress}
          onChange={(e) => setKeyword(e.target.value.slice(0, 127))}
          rightElement={null}
          fullWidth
        >
          {(!keyword || keyword.length === 0) && (
            <Placeholder>{t('hc.search.question_keywords')}</Placeholder>
          )}
        </StyledSelectSearch>

        {articles && articles.articles && (
          <SelectList>
            {articles.articles.map((article) => (
              <SelectOption
                value={[article.category.id, article.id]}
                key={article.id}
              >
                <ElkHighlighter text={article.title} />
              </SelectOption>
            ))}
          </SelectList>
        )}
      </Select>
    </StyledSelectSearchWrapper>
  );
}

NavSearchField.propTypes = {
  style: object,
};

NavSearchField.defaultProps = {
  style: {},
};

export default NavSearchField;
